import { createAction, createReducer } from "@reduxjs/toolkit"
import { defineMessages } from "react-intl"
import httpClient, { CancelToken } from "./httpClient"
import { actions as notifier } from "../component/notifier"
import { asBoolean } from "../commons/utils"
import licenseSlice from "../pages/admin/licensing/licenseSlice"
import esignSlice from "./esignSlice"
import widgetController from "./widgetSlice"
import { STAFF } from "../commons/userTypes"

const { actions: esign } = esignSlice

const initialState = {
  sessionChecking: true,
  loggedIn: false,
  user: undefined,
  remainingTries: undefined,
  error: undefined
}

const messages = defineMessages({
  loggedIn: {
    id: "action.login.message",
    defaultMessage: "Login successful"
  },
  notCreatedAccount: {
    id: "user.register.error.token",
    defaultMessage:
      // "Nous n'avons pas pu finaliser la création de votre compte. Veuillez vérifier votre ancien mot de passe. Néanmoins si vous pensez qu'il s'agit d'une erreur n'hésitez pas à nous contacter."
      "We cannot finalize your account creation. Could you check your old password. However, if you thing that there is an issue, do not hesitate to contact us.."
  }
})

const SESSION_CHECKING = createAction("SESSION_CHECKING")
const USER_SESSION_CHECKING = createAction("USER_SESSION_CHECKING")
const SET_REMAINING_TRIES = createAction("SET_REMAINING_TRIES")
const SET_ERROR_MESSAGE = createAction("SET_ERROR_MESSAGE")
const SET_UNSUBSCRIPTION_SCOPES = createAction("SET_UNSUBSCRIPTION_SCOPES")

function adminIsLoggedIn(sessionData) {
  const isLoggedIn = sessionData?.loggedIn ?? false
  const isStaff = sessionData?.user?.type === STAFF ?? false

  return isLoggedIn && !isStaff
}

function clearLocalSession() {
  return (dispatch) => {
    dispatch(
      USER_SESSION_CHECKING({
        ...initialState,
        sessionChecking: false
      })
    )
  }
}

function checkingSession() {
  return async (dispatch) => {
    dispatch(SESSION_CHECKING(true))
    const { token, cancel: cancelRequests } = CancelToken.source()
    try {
      const { data: sessionData } = await httpClient.get("/auth/session")
      dispatch(USER_SESSION_CHECKING(sessionData))
      await dispatch(licenseSlice.actions.fetchLicenseConfiguration())
      if (sessionData?.loggedIn) {
        await dispatch(esign.fetchEsignConfig(token))
      }
      if (!adminIsLoggedIn(sessionData)) {
        return
      }
      dispatch(licenseSlice.actions.checkLicenseAgreement())
    } finally {
      cancelRequests()
      dispatch(SESSION_CHECKING(false))
    }
  }
}

function logout() {
  return async (dispatch) => {
    const { enablePreview, clearWidgetMessage, clearWealthSelector } =
      widgetController.actions

    dispatch(enablePreview(false))
    await httpClient.post("/auth/logout")
    dispatch(USER_SESSION_CHECKING({ loggedIn: false, user: undefined }))
    dispatch(clearWidgetMessage())
    dispatch(clearWealthSelector())
  }
}

function login(credentials) {
  return (dispatch) =>
    httpClient
      .post("/auth/login", {
        email: credentials.email,
        password: credentials.password
      })
      .then(({ data }) => {
        const sessionData = { loggedIn: true, user: data.user }
        dispatch(USER_SESSION_CHECKING(sessionData))
        dispatch(SET_REMAINING_TRIES(undefined))
        dispatch(notifier.enqueueMessage(messages.loggedIn))

        if (!adminIsLoggedIn(sessionData)) {
          return
        }

        dispatch(licenseSlice.actions.checkLicenseAgreement())
      })
      .catch((err) => {
        const remainingTries = err.remainingTries || 0
        dispatch(SET_REMAINING_TRIES(remainingTries))
      })
}

function register(token, values) {
  return (dispatch) =>
    httpClient
      .post("/api/invite/register", { token, ...values })
      .then(({ data }) => {
        const email = data.primaryEmail
        const { password } = values
        return dispatch(login({ email, password }))
      })
      .catch(() => {
        dispatch(SET_ERROR_MESSAGE(messages.notCreatedAccount))
      })
}

function changePassword(args) {
  return () => httpClient.post("/auth/changepassword", args)
}

function checkPassword(password) {
  return () => httpClient.post(`/auth/checkpassword`, { password })
}

function resetPassword(args) {
  return () => httpClient.post("/auth/resetpassword", args)
}

function clearError() {
  return (dispatch) => dispatch(SET_ERROR_MESSAGE(undefined))
}

function updateUnsubscriptionScopes(unsubscriptionScopes, cancelToken) {
  return async (dispatch) => {
    const { data } = await httpClient.put(
      "/api/user/subscription-preferences",
      { unsubscriptionScopes },
      { cancelToken }
    )

    dispatch(SET_UNSUBSCRIPTION_SCOPES(data.unsubscriptionScopes))
  }
}

const reducer = createReducer(initialState, {
  SESSION_CHECKING: (state, { payload }) => ({
    ...state,
    sessionChecking: asBoolean(payload)
  }),
  USER_SESSION_CHECKING: (state, { payload }) => ({
    ...state,
    loggedIn: payload.loggedIn,
    user: payload.user
  }),
  SET_REMAINING_TRIES: (state, { payload }) => ({
    ...state,
    remainingTries: payload
  }),
  SET_ERROR_MESSAGE: (state, { payload }) => ({
    ...state,
    error: payload
  }),
  SET_UNSUBSCRIPTION_SCOPES: (state, { payload }) => ({
    ...state,
    user: {
      ...state.user,
      unsubscriptionScopes: payload.unsubscriptionScopes
    }
  })
})

export default {
  initialState,
  actions: {
    checkingSession,
    clearLocalSession,
    login,
    logout,
    register,
    changePassword,
    resetPassword,
    clearError,
    updateUnsubscriptionScopes,
    checkPassword
  },
  reducer
}
